import { useState } from 'react';
import { CloseOutlined, ThunderboltFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Flex, Grid, Typography, theme } from 'antd';
import { useLocation } from 'react-router';
import { api } from '../api';
import { registrarEvento } from '../analytics';
import { contar, palavra } from '../texto';
import { useAbrirOferta } from './Oferta';

/**
 * A faixa do plano gratuito, no topo de tudo.
 *
 * É o recurso que a Voa usa e que faltava aqui: quem não assina vê, em toda
 * tela, quanto ainda tem na semana e o botão que resolve. O paywall antigo só
 * aparecia **depois** de a cota acabar - quer dizer, o produto só falava de
 * plano no pior momento possível, quando a pessoa já tinha sido interrompida.
 *
 * Ela some sozinha para quem comprou: acesso ativo não precisa de faixa, e
 * deixá-la ali transformaria a compra em algo que não muda nada.
 *
 * **O layout precisa saber se ela está no ar** para descer a barra lateral, que
 * é fixa. Por isso a decisão sai num hook, chamado uma vez pelo `AppLayout`, e
 * não de dentro do componente avisando o pai durante o render - que é
 * justamente o `setState` em cascata que o React proíbe.
 */

/** Enquanto a faixa couber nesta altura, a barra lateral se encaixa sem medir nada. */
export const ALTURA_DO_AVISO = 40;

/**
 * Dispensar vale **até o próximo carregamento**, e não pela semana.
 *
 * Ela guardava o dia de Brasília no `localStorage`, e a intenção era boa: fechar
 * e estudar em paz até a cota virar. Na prática isso desligava a faixa por 24
 * horas depois de um clique - e, com a cota semanal, desligaria por até sete
 * dias. **Ela existe justamente para incomodar um pouco**, porque é a única
 * coisa na tela que explica por que o estudo trava no meio da tarde e o único
 * lugar de onde a oferta nasce sem interromper ninguém.
 *
 * Agora a dispensa é só estado de componente: some enquanto a pessoa está lendo
 * o que estava lendo, atravessa a navegação dentro do app, e volta no próximo
 * carregamento da página. É o meio-termo honesto - nunca prende ninguém na tela
 * em que está, nunca some por uma semana inteira.
 */
export interface AvisoDoPlano {
  mostrar: boolean;
  /**
   * A pessoa não assina nada - o fato, sem a pergunta de mostrar a faixa.
   *
   * Os dois são diferentes e os dois têm uso: a faixa se dispensa e some na
   * tela de Plano, e nenhuma das duas coisas apaga o plano de quem está ali.
   * Quem quer marcar a conta como gratuita (a etiqueta do rodapé da barra)
   * pergunta isto, e não `mostrar`. Nasce `false` enquanto a resposta não
   * chega: etiqueta que aparece depois é ruim, mas etiqueta que aparece e
   * some é pior.
   */
  gratuito: boolean;
  /**
   * A pessoa tem algum acesso ativo.
   *
   * **Não é o contrário de `gratuito`**: enquanto a resposta não chega, os dois
   * são `false`. É o que permite ao rodapé da barra não carimbar ninguém antes
   * de saber - nem "Grátis" para quem paga, nem "Pro" para quem não paga.
   */
  assinante: boolean;
  /** Quanto ainda resta na semana, para a frase. */
  restante: { answers: number; commentaries: number };
  dispensar: () => void;
}

export function useAvisoDoPlanoGratuito(): AvisoDoPlano {
  const location = useLocation();
  const [dispensado, setDispensado] = useState(false);

  const status = useQuery({ queryKey: ['study-status'], queryFn: () => api.studyStatus() });

  const assinante = (status.data?.acessos ?? []).some((acesso) => acesso.ativo);
  /*
   * Na tela de Plano, não. Lá a situação da conta já está escrita por extenso,
   * com o botão que abre a mesma oferta - a faixa repetiria as duas coisas a
   * dois centímetros delas.
   */
  const naVitrine = location.pathname.startsWith('/configuracoes/plano');

  const gratuito = Boolean(status.data) && !assinante;

  return {
    mostrar: gratuito && !naVitrine && !dispensado,
    gratuito,
    assinante: Boolean(status.data) && assinante,
    restante: status.data?.restante ?? { answers: 0, commentaries: 0 },
    dispensar: () => setDispensado(true),
  };
}

export function AvisoDoPlanoGratuito({ restante, dispensar }: AvisoDoPlano) {
  const { token } = theme.useToken();
  const abrirOferta = useAbrirOferta();
  const telas = Grid.useBreakpoint();

  const acabou = restante.answers === 0;

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={12}
      role="status"
      style={{
        height: ALTURA_DO_AVISO,
        paddingInline: 16,
        /*
         * Amarelo enquanto há cota, vermelho quando acaba. A cor é a própria
         * informação: quem olha de relance já sabe se pode continuar sem ler a
         * frase.
         */
        background: acabou ? token.colorErrorBg : token.colorWarningBg,
        borderBottom: `1px solid ${acabou ? token.colorErrorBorder : token.colorWarningBorder}`,
        /*
         * Grudada no topo, e não rolando com a página. A barra lateral é fixa
         * e começa exatamente embaixo dela: se a faixa subisse com a rolagem,
         * sobrariam quarenta pixels de vazio acima do menu.
         */
        position: 'sticky',
        top: 0,
        // Acima da barra lateral fixa, que encosta nela por baixo.
        zIndex: 11,
      }}
    >
      <Flex align="center" gap={8} style={{ minWidth: 0 }}>
        <ThunderboltFilled
          style={{ color: acabou ? token.colorError : token.colorWarning, flexShrink: 0 }}
        />
        {/*
          Duas frases, e não uma cortada. Medido em 390px: a frase inteira
          truncava em "Plano gratuito: restam 3 quest...", que é pior que dizer
          menos - some justamente o número, que é a informação. No celular fica
          só o que muda; o resto a pessoa lê em Assinatura.
        */}
        <Typography.Text ellipsis style={{ fontSize: token.fontSizeSM }}>
          {acabou ? (
            <>
              Sua cota da semana acabou.
              {telas.sm ? ' Libere um módulo e estude sem limite semanal.' : null}
            </>
          ) : telas.sm ? (
            <>
              Plano gratuito: restam{' '}
              <strong>{contar(restante.answers, 'questão', 'questões')}</strong> e{' '}
              <strong>{contar(restante.commentaries, 'resolução', 'resoluções')}</strong> nesta semana.
            </>
          ) : (
            <>
              {/*
                Aqui o sujeito é um só, então o verbo concorda com ele: "Resta 1
                questão nesta semana". Na frase de cima o sujeito é composto ("1 questão
                e nenhuma resolução") e o plural continua certo - por isso só
                este ramo mudou.
              */}
              {palavra(restante.answers, 'Resta', 'Restam')}{' '}
              <strong>{contar(restante.answers, 'questão', 'questões')}</strong> nesta semana.
            </>
          )}
        </Typography.Text>
      </Flex>

      <Flex align="center" gap={4} style={{ flexShrink: 0 }}>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            registrarEvento({ nome: 'plan_banner_clicked', restante: restante.answers });
            /*
              Abre a oferta por cima, e não navega. A faixa aparece em toda
              tela, inclusive no meio de uma questão: tirar a pessoa dali para
              mostrar um preço custava o lugar onde ela estava estudando.
            */
            abrirOferta();
          }}
        >
          Ver os planos
        </Button>
        <Button
          size="small"
          type="text"
          aria-label="Dispensar o aviso"
          icon={<CloseOutlined />}
          onClick={dispensar}
        />
      </Flex>
    </Flex>
  );
}
